import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Play, 
  Loader2, 
  Video, 
  RefreshCw, 
  CheckCircle,
  Clock,
  Sparkles,
  X
} from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

// Tutorial video definitions with titles and descriptions
const TUTORIAL_DEFINITIONS = {
  client: {
    booking: {
      title: "How to Book an Appointment",
      description: "Learn how to find a stylist and book your perfect appointment",
      duration: "30 sec"
    },
    payment: {
      title: "Payment Options Explained",
      description: "Card payments, CashApp, Zelle, and receipt uploads",
      duration: "30 sec"
    },
    loyalty: {
      title: "Earning & Using Loyalty Points",
      description: "Maximize your rewards with every visit",
      duration: "30 sec"
    },
    review: {
      title: "Leaving Reviews",
      description: "Help others and earn bonus points",
      duration: "30 sec"
    }
  },
  stylist: {
    profile: {
      title: "Setting Up Your Profile",
      description: "Create a professional profile that attracts clients",
      duration: "30 sec"
    },
    services: {
      title: "Managing Services & Pricing",
      description: "Add services, set prices, and create upsells",
      duration: "30 sec"
    },
    payments: {
      title: "Configuring Payment Methods",
      description: "Set up CashApp, Zelle, and PayPal",
      duration: "30 sec"
    },
    receipts: {
      title: "Verifying Payment Receipts",
      description: "Approve client payment confirmations",
      duration: "30 sec"
    },
    gallery: {
      title: "Building Your Portfolio",
      description: "Showcase your best work",
      duration: "30 sec"
    }
  },
  owner: {
    booth: {
      title: "Managing Booth Renters",
      description: "Add renters and track rent payments",
      duration: "30 sec"
    },
    revenue: {
      title: "Revenue & Analytics",
      description: "Track your salon's performance",
      duration: "30 sec"
    },
    staff: {
      title: "Staff Management",
      description: "Invite and manage your team",
      duration: "30 sec"
    }
  }
};

const VideoTutorialCard = ({ tutorialKey, roleKey, video, onGenerate, isGenerating }) => {
  const [showVideo, setShowVideo] = useState(false);
  const definition = TUTORIAL_DEFINITIONS[roleKey]?.[tutorialKey] || {};
  
  const getStatusBadge = () => {
    switch (video?.status) {
      case "ready":
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Ready</Badge>;
      case "generating":
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 animate-pulse">Generating...</Badge>;
      case "failed":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Failed</Badge>;
      default:
        return <Badge className="bg-stone-500/20 text-stone-400 border-stone-500/30">Not Generated</Badge>;
    }
  };

  return (
    <>
      <Card className="bg-stone-800/50 border-stone-700 hover:border-stone-600 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Video className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h4 className="text-white font-medium text-sm truncate">{definition.title || tutorialKey}</h4>
                {getStatusBadge()}
              </div>
              <p className="text-stone-400 text-xs mb-3">{definition.description || "Tutorial video"}</p>
              
              <div className="flex items-center gap-2">
                {video?.status === "ready" && video?.url ? (
                  <Button
                    size="sm"
                    onClick={() => setShowVideo(true)}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid={`play-video-${tutorialKey}`}
                  >
                    <Play className="w-3 h-3 mr-1" />
                    Watch
                  </Button>
                ) : video?.status === "generating" ? (
                  <Button size="sm" disabled className="bg-stone-700 text-stone-400">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Generating...
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => onGenerate(tutorialKey, roleKey)}
                    disabled={isGenerating}
                    className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"
                    data-testid={`generate-video-${tutorialKey}`}
                  >
                    {isGenerating ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Sparkles className="w-3 h-3 mr-1" />
                    )}
                    Generate with AI
                  </Button>
                )}
                
                {video?.status === "failed" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onGenerate(tutorialKey, roleKey)}
                    disabled={isGenerating}
                    className="border-stone-600 text-stone-300"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Retry
                  </Button>
                )}
              </div>
              
              <div className="flex items-center gap-2 mt-2 text-xs text-stone-500">
                <Clock className="w-3 h-3" />
                <span>{definition.duration || "30 sec"}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Video Player Modal */}
      <Dialog open={showVideo} onOpenChange={setShowVideo}>
        <DialogContent className="max-w-3xl bg-stone-900 border-stone-700 p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="text-white flex items-center gap-2">
              <Video className="w-5 h-5 text-violet-400" />
              {definition.title || tutorialKey}
            </DialogTitle>
          </DialogHeader>
          <div className="p-4">
            {video?.url && (
              <video
                src={video.url}
                controls
                autoPlay
                className="w-full rounded-lg"
                style={{ maxHeight: "60vh" }}
              >
                Your browser does not support video playback.
              </video>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const VideoTutorials = ({ userRole = "client" }) => {
  const { token } = useAuth();
  const [videos, setVideos] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);

  useEffect(() => {
    fetchVideos();
  }, [userRole]);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/ai/tutorial-videos?user_role=${userRole}`);
      setVideos(response.data[userRole] || {});
    } catch (error) {
      console.error("Failed to fetch tutorial videos:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateVideo = async (tutorialKey, roleKey) => {
    if (!token) {
      toast.error("Please log in to generate videos");
      return;
    }

    setGenerating(tutorialKey);
    try {
      const response = await axios.post(
        `${API}/ai/generate-tutorial-video`,
        {
          prompt: "",
          tutorial_type: tutorialKey,
          user_role: roleKey,
          duration: 4,
          size: "1280x720"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Video generation started! This may take 2-5 minutes.");
      
      // Update local state
      setVideos(prev => ({
        ...prev,
        [tutorialKey]: {
          ...prev[tutorialKey],
          status: "generating",
          video_id: response.data.video_id
        }
      }));

      // Poll for completion
      pollVideoStatus(response.data.video_id, tutorialKey);
    } catch (error) {
      toast.error("Failed to start video generation");
      console.error("Video generation error:", error);
    } finally {
      setGenerating(null);
    }
  };

  const pollVideoStatus = async (videoId, tutorialKey) => {
    const maxAttempts = 60; // 5 minutes with 5-second intervals
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/ai/tutorial-video/${videoId}`);
        
        if (response.data.status === "ready") {
          setVideos(prev => ({
            ...prev,
            [tutorialKey]: response.data
          }));
          toast.success("Tutorial video is ready!");
          return;
        } else if (response.data.status === "failed") {
          setVideos(prev => ({
            ...prev,
            [tutorialKey]: { ...prev[tutorialKey], status: "failed" }
          }));
          toast.error("Video generation failed. Please try again.");
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000);
        }
      } catch (error) {
        console.error("Error polling video status:", error);
      }
    };

    setTimeout(checkStatus, 10000); // First check after 10 seconds
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
      </div>
    );
  }

  const tutorials = TUTORIAL_DEFINITIONS[userRole] || {};

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
          <Video className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className="text-white font-semibold">Video Tutorials</h3>
          <p className="text-xs text-stone-400">AI-generated walkthroughs powered by Sora 2</p>
        </div>
        <Badge className="ml-auto bg-violet-500/20 text-violet-400 border-violet-500/30">
          <Sparkles className="w-3 h-3 mr-1" />
          AI Generated
        </Badge>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {Object.keys(tutorials).map(key => (
          <VideoTutorialCard
            key={key}
            tutorialKey={key}
            roleKey={userRole}
            video={videos[key]}
            onGenerate={generateVideo}
            isGenerating={generating === key}
          />
        ))}
      </div>

      <p className="text-xs text-stone-500 text-center mt-4">
        Video generation takes 2-5 minutes. Videos are stored and reusable.
      </p>
    </div>
  );
};

export default VideoTutorials;
